import { useCallback, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { ListFilterIcon, PlusIcon } from "lucide-react";

import { PitchFilterSheet } from "@/components/filters/PitchFilterSheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserContext } from "../../../services/firebase";
import type { Booking, Campaign, Pitch, User } from "../../../shared/types";
import { emptyPitchStatusFilter, pitchStatusFilterCount, type PitchStatusFilter } from "../../../utils/pitching/filter";
import { canPitchCreators } from "../../../utils/pitching/permissions";
import { defaultDetailTab, type DetailTab } from "../campaign";
import { BookingsTable } from "./BookingsTable";
import { PitchesTable } from "./PitchesTable";

type CampaignTabsProps = {
    campaign: Campaign;
    booker?: User;
    pitches: Pitch[];
    pitchesLoading: boolean;
    bookings: Booking[];
    bookingsLoading: boolean;
};

/** Pitches and bookings come from the page. Each table renders its own rows. */
export function CampaignTabs({
    campaign, booker, pitches, pitchesLoading, bookings, bookingsLoading,
}: CampaignTabsProps) {
    const campaignId = campaign.id;
    const user = useContext(UserContext);
    const [tabOverride, setTabOverride] = useState<DetailTab | null>(null);
    const onApproved = useCallback(() => setTabOverride("bookings"), []);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<PitchStatusFilter>(emptyPitchStatusFilter);
    const filterCount = pitchStatusFilterCount(statusFilter);

    const showAddPitch = canPitchCreators(user, campaign);
    const defaultTab = defaultDetailTab(user, campaign, pitches);
    const tab = tabOverride ?? defaultTab;

    return (
        <Tabs value={tab} onValueChange={(next) => setTabOverride(next as DetailTab)} className="gap-3">
            {/* Tabs and Add Pitch share one row. */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <TabsList>
                    <TabsTrigger value="pitches">Pitches</TabsTrigger>
                    <TabsTrigger value="bookings">Bookings</TabsTrigger>
                </TabsList>
                {/* Filter and Add Pitch sit side by side on the right of the tabs row. */}
                <div className="flex flex-wrap items-center gap-4">
                    {tab === "pitches" ? (
                        <Button
                            type="button"
                            size="xs"
                            variant="secondary"
                            className="relative"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <ListFilterIcon data-icon="inline-start" />
                            Filter
                            {filterCount > 0 ? (
                                <Badge variant="blue" className="pointer-events-none absolute -top-2 -right-3">
                                    {filterCount}
                                </Badge>
                            ) : null}
                        </Button>
                    ) : null}
                    {showAddPitch ? (
                        <Button size="xs" render={<Link to={`/campaign/${campaignId}/pitch`} />}>
                            <PlusIcon data-icon="inline-start" />
                            Add Pitch
                        </Button>
                    ) : null}
                </div>
            </div>

            <TabsContent value="pitches" keepMounted>
                <PitchesTable
                    campaign={campaign}
                    booker={booker}
                    pitches={pitches}
                    pitchesLoading={pitchesLoading}
                    bookings={bookings}
                    statusFilter={statusFilter}
                    onApproved={onApproved}
                />
            </TabsContent>
            <TabsContent value="bookings" keepMounted>
                <BookingsTable campaign={campaign} bookings={bookings} bookingsLoading={bookingsLoading} />
            </TabsContent>
            <PitchFilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={statusFilter}
                onChange={setStatusFilter}
            />
        </Tabs>
    );
}
